// A8-i (I.160): balanço de estoque de um depósito (pacote docs/pacotes/A8-i.md). Tudo pelo banco:
// abrir, mudar a data, cancelar e decretar só o master; o painel, master ou quem tem conferência.
// types.ts só é regenerado depois do APPLY do A8-i: as chamadas usam cast documentado.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type StockBalanceList = 'not_counted' | 'no_cost';

export interface StockBalanceRow {
  product_id: string;
  name: string;
  sku: string | null;
  quantity: number;
  negative: boolean;
}

export interface StockBalancePart {
  id: string;
  name: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  access_token: string;
}

export interface StockBalanceProgress {
  balance: {
    id: string;
    name: string;
    status: 'open' | 'decreed' | 'cancelled';
    deposit_id: string;
    deposit_name: string | null;
    counts_valid_since: string;
    opened_at: string;
    opening_summary: { in_scope?: number; stock_value_before?: number } | null;
  } | null;
  stats?: {
    in_scope: number;
    counted: number;
    not_counted: number;
    not_counted_negative: number;
    no_cost: number;
  };
  page?: number;
  page_size?: number;
  rows?: StockBalanceRow[];
  parts?: StockBalancePart[];
}

const rpc = (name: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (n: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>)(name, args);

const errorMessage = (err: unknown, fallback: string) =>
  (err as { message?: string } | null)?.message || fallback;

export const STOCK_BALANCE_PAGE_SIZE = 50;

export function useStockBalanceProgress(list: StockBalanceList, page: number) {
  const { company } = useAuth();
  return useQuery({
    queryKey: ['stock-balance', company?.id, list, page],
    queryFn: async () => {
      const { data, error } = await rpc('get_stock_balance_progress', {
        p_balance_id: null,
        p_list: list,
        p_page: page,
        p_page_size: STOCK_BALANCE_PAGE_SIZE,
      });
      if (error) throw error;
      return data as StockBalanceProgress;
    },
    enabled: !!company?.id,
    staleTime: 30 * 1000,
  });
}

export function useStockBalanceActions() {
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['stock-balance'] });
    queryClient.invalidateQueries({ queryKey: ['stock-checklists'] });
  };

  const open = useMutation({
    mutationFn: async (input: { name: string; depositId: string; validSince: string }) => {
      const { error } = await rpc('open_stock_balance', {
        p_name: input.name,
        p_deposit_id: input.depositId,
        p_counts_valid_since: input.validSince,
      });
      if (error) throw error;
    },
    onSuccess: () => { toast.success('Balanço aberto'); refresh(); },
    onError: (err) => toast.error(errorMessage(err, 'Erro ao abrir balanço')),
  });

  const setValidSince = useMutation({
    mutationFn: async (input: { balanceId: string; validSince: string }) => {
      const { error } = await rpc('set_stock_balance_valid_since', {
        p_balance_id: input.balanceId,
        p_counts_valid_since: input.validSince,
      });
      if (error) throw error;
    },
    onSuccess: () => { toast.success('Data do balanço atualizada'); refresh(); },
    onError: (err) => toast.error(errorMessage(err, 'Erro ao mudar a data')),
  });

  const cancel = useMutation({
    mutationFn: async (balanceId: string) => {
      const { error } = await rpc('cancel_stock_balance', { p_balance_id: balanceId });
      if (error) throw error;
    },
    onSuccess: () => { toast.success('Balanço cancelado'); refresh(); },
    onError: (err) => toast.error(errorMessage(err, 'Erro ao cancelar balanço')),
  });

  const decree = useMutation({
    mutationFn: async (balanceId: string) => {
      const { error } = await rpc('decree_stock_balance', { p_balance_id: balanceId });
      if (error) throw error;
    },
    onSuccess: () => { toast.success('Balanço decretado'); refresh(); },
    onError: (err) => toast.error(errorMessage(err, 'Erro ao decretar balanço')),
  });

  return { open, setValidSince, cancel, decree };
}
